import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Drawer, rememberStudioPath } from '@vanguard-atlas/shared';
import { ErrorBoundary } from 'shared/components/errors';
import { Sidebar } from './Sidebar';
import { Header } from './Header';

/**
 * Main application layout with sidebar and header
 *
 * Features:
 * - Responsive sidebar (collapsible on desktop, drawer on mobile)
 * - Fixed header with navigation and actions
 * - Outlet for nested routes
 * - Persists the current Studio path so the Chat → Studio switcher can
 *   return the user to where they left off.
 */
export function MainLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    rememberStudioPath(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return (
    <div className="h-screen flex bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar 
          collapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
      </div>

      {/* Mobile Sidebar — shared Drawer (ADR-114) */}
      <Drawer
        open={mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
        side="left"
        width="w-[85%] max-w-[320px]"
        showClose={false}
        data-testid="mobile-sidebar"
        className="bg-card p-0"
      >
        <Sidebar onToggle={() => setMobileSidebarOpen(false)} />
      </Drawer>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <Header 
          showMenuButton 
          onMenuClick={() => setMobileSidebarOpen(true)} 
        />
        
        <main className="flex-1 overflow-y-auto">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
